import { DASHBOARD_PAGE, BUCKET_META_PAGE, BUCKET_FEATURE_PAGES, ISSUE_LIST_PAGE } from '@/router'
import store from '@/store'
import { BACKEND_APP_TYPE } from '@/config/appTypes'

function hasCurrentAppType(route) {
  const appType = (route.params && route.params.appType) || store.getters.currentAppType
  return !appType || !route.meta.appTypes || route.meta.appTypes.includes(appType)
}
export function isRouteUserFacing(route) {
  if (!route.meta) throw new Error(`Route ${route.name} has no meta object`)

  // Always hide hidden routes
  if (route.meta.hide) return false

  if (!hasCurrentAppType(route)) return false

  // If the front is on dedicated infrastructure, don't show hidden routes
  if (store.getters.isDedicated && route.meta.hideWhenDedicated) return false

  // If the current bucket have the feature, show it
  if (hasFeature(route)) return true

  return false
}

/**
 * Get all the routes that may be in the navigation menu.  `isRouteUserFacing` should be used to
 * test if it is indeed available to the user.
 */
export function getMenuRoutes(router) {
  return getRouteByName(router.options.routes, BUCKET_FEATURE_PAGES)
    .children.map(route => (route.children ? route.children.find(r => r.path === '/') : route))
    .filter(route => !route.meta.hideFromMenu && hasCurrentAppType(route))
}

/**
 * Get Plus and Enterprise incentives.  An incentive is an object associating a route and a feature.
 *
 * Since a feature can be used in multiple routes (ex: the 'profiling' feature is used for both 'CPU
 * Profiling' and 'Memory Snapshot' pages), multiple incentives may be about the same feature.
 *
 * Similarly, since a route can use multiple features (ex: the 'Logs' page uses the features 'logs'
 * and 'realtimeLogsPage'), multiple incentives may be about the same route.
 *
 * An id is conveniently generated to target an incentive accross pages.
 */
export function getIncentives(router, { skipEnabled = false } = {}) {
  const result = { plus: [], enterprise: [] }

  // No incentive for enterprise/dedicated
  if (store.getters.isDedicated || store.getters.currentPlanType === 'enterprise') return result

  let id = 0
  for (const route of getMenuRoutes(router)) {
    if (!route.meta.skipIncentive) {
      const featureIds = !route.meta.featureId
        ? []
        : Array.isArray(route.meta.featureId)
        ? route.meta.featureId
        : [route.meta.featureId]
      for (const feature of featureIds) {
        id += 1
        if (store.getters.freeFeatures[feature]) continue
        if (skipEnabled && store.getters.hasFeature({ feature })) continue
        const incentive = {
          id,
          feature,
          route,
        }
        // Customize those fields with the 'byFeature' route meta
        ;['doc', 'title', 'abstract', 'screen'].forEach(field => {
          incentive[field] =
            (route.meta.byFeature && route.meta.byFeature[feature][field]) || route.meta[field]
        })

        if (store.getters.plusFeatures[feature]) {
          result.plus.push(incentive)
        } else {
          result.enterprise.push(incentive)
        }
      }
    }
  }

  return result
}

export const hasFeature = route => {
  if (!route.meta || !route.meta.featureId) return true
  if (Array.isArray(route.meta.featureId)) {
    return route.meta.featureId.some(feature => store.getters.hasFeature({ feature }))
  }
  return store.getters.hasFeature({ feature: route.meta.featureId })
}

export function getRouteByName(routes, name) {
  return getRouteBy(routes, route => route.name === name)
}

export function getRouteBy(routes, condition) {
  let found
  routes.some(route => {
    if (condition(route)) {
      found = route
    } else if (route.children) {
      found = getRouteBy(route.children, condition)
    }
    return found
  })
  return found
}

export function getLinkToApp(appName) {
  // If the current route already is an 'app page', only change the query app parameter
  if (store.state.route.meta.requireApp) {
    return {
      query: {
        ...store.state.route.query,
        app: appName,
      },
    }
  }

  // Else, go to the app main page
  const name = store.getters.currentAppType === BACKEND_APP_TYPE ? DASHBOARD_PAGE : ISSUE_LIST_PAGE
  return { name, query: { app: appName } }
}

export function getLinkToBucket(bucketId) {
  const appType = store.getters.currentAppType

  if (appType) {
    return { name: BUCKET_FEATURE_PAGES, params: { id: bucketId, appType } }
  }

  return { name: BUCKET_META_PAGE, params: { id: bucketId } }
}

export const getRouteIndex = (router, routeName) => {
  const routes = router.options.routes.slice()
  let index = 0
  while (routes.length) {
    const route = routes.shift()
    if (route.name === routeName) return index
    if (route.children) routes.unshift(...route.children)
    index += 1
  }
  return -1
}
